import { useEffect, useState, useRef, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { NutritionPlan } from '../types';

export type DietStatus = 'idle' | 'generating_initial' | 'processing' | 'updating' | 'trigger_update' | 'completed' | 'error_generation' | 'error_finalization' | 'pending_photos';

interface UseDietStatusReturn {
    status: DietStatus;
    plan: NutritionPlan | null;
    isLoading: boolean;
    isSyncingCloud: boolean;
    hasValidPlan: boolean;
    isGenerating: boolean;
    processStartTime: number | null;
    targetCompletionTime: number | null;
    generationType: 'new' | 'update' | null;
    needsDietReveal: boolean;
    triggerGeneration: (isUpdate?: boolean) => Promise<void>;
    resetStatus: () => Promise<void>;
    clearDietReveal: () => Promise<void>;
}

export const useDietStatus = (): UseDietStatusReturn => {
    const { user, subscriptionStatus, loading: authLoading } = useAuth();
    const isPremium = subscriptionStatus === 'active' || subscriptionStatus === 'trialing' || subscriptionStatus === 'pro' || localStorage.getItem('aura_demo_mode') === 'true';

    const [currentTime, setCurrentTime] = useState(Date.now());

    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentTime(Date.now());
        }, 1000);
        return () => clearInterval(interval);
    }, []);

    // State is exclusively managed by React and Supabase.
    // LocalStorage is ONLY used as a transient read-only cache for the very first render to avoid Skeleton flashes.
    const [status, setStatus] = useState<DietStatus>(() => {
        try {
            // V705: Enforce pending_photos if premium, has no photos and no plan
            const isPremiumLocal = localStorage.getItem('aura_subscription_status') === 'active' ||
                localStorage.getItem('aura_subscription_status') === 'pro' ||
                localStorage.getItem('aura_subscription_status') === 'trialing' ||
                localStorage.getItem('aura_demo_mode') === 'true';

            const cachedUserData = localStorage.getItem('aura_user_data');
            let hasPhotos = false;
            let hasPlan = false;
            if (cachedUserData) {
                try {
                    const parsed = JSON.parse(cachedUserData);
                    hasPhotos = !!(parsed.photos?.front && parsed.photos?.side && parsed.photos?.back);
                    hasPlan = !!(parsed.nutritionPlan || parsed.diet);
                } catch (e) {}
            }

            if (isPremiumLocal && !hasPhotos && !hasPlan) {
                return 'pending_photos';
            }

            // First check flat localStorage key to prevent stale/deleted auth context flash
            const flatStatus = localStorage.getItem('aura_diet_status');
            if (flatStatus) {
                if (flatStatus === 'pending_photos' && hasPlan) {
                    return 'completed';
                }
                // STALE CACHE GUARD for flat status
                if (flatStatus === 'processing' || flatStatus === 'updating' || flatStatus === 'generating_initial' || flatStatus === 'trigger_update') {
                    const flatTarget = localStorage.getItem('aura_target_completion_time');
                    const targetTime = flatTarget ? parseInt(flatTarget) : null;
                    if (targetTime && Date.now() >= targetTime) {
                        console.log("[useDietStatus] Stale cache detected on flat key. Assuming 'completed' instead of", flatStatus);
                        return 'completed';
                    }
                }
                return flatStatus as DietStatus;
            }

            const cached = localStorage.getItem('aura_user_data');
            if (cached) {
                const parsed = JSON.parse(cached);
                const cachedStatus = parsed.aura_diet_status || 'idle';
                if (cachedStatus === 'pending_photos' && hasPlan) {
                    return 'completed';
                }
                
                // V6032: STALE CACHE GUARD
                if (cachedStatus === 'processing' || cachedStatus === 'updating' || cachedStatus === 'generating_initial' || cachedStatus === 'trigger_update') {
                    const parsedTarget = parsed.aura_target_completion_time;
                    const flatTarget = localStorage.getItem('aura_target_completion_time');
                    const targetTime = parsedTarget ? parsedTarget : (flatTarget ? parseInt(flatTarget) : null);
                    
                    if (targetTime && Date.now() >= targetTime) {
                        console.log("[useDietStatus] Stale cache detected. Assuming 'completed' instead of", cachedStatus);
                        return 'completed';
                    }
                }
                
                return cachedStatus;
            }
        } catch (e) {}
        return 'idle';
    });
    
    const [plan, setPlan] = useState<NutritionPlan | null>(() => {
        try {
            if (user?.id) {
                const cachedPlanStr = localStorage.getItem(`aura_current_diet_${user.id}`);
                if (cachedPlanStr) {
                    let parsed = JSON.parse(cachedPlanStr);
                    if (typeof parsed === 'string') parsed = JSON.parse(parsed);
                    return parsed;
                }
            }
        } catch (e) {
            console.error("Failed to parse cached plan:", e);
        }
        return null;
    });
    
    const [processStartTime, setProcessStartTime] = useState<number | null>(() => {
        try {
            const flatStart = localStorage.getItem('aura_diet_process_start');
            if (flatStart && flatStart !== 'null' && flatStart !== 'undefined') {
                return parseInt(flatStart, 10);
            }
            const cached = localStorage.getItem('aura_user_data');
            if (cached) return JSON.parse(cached).aura_diet_process_start || null;
        } catch (e) {}
        return null;
    });

    const [targetCompletionTime, setTargetCompletionTime] = useState<number | null>(() => {
        try {
            const flatTarget = localStorage.getItem('aura_target_completion_time');
            if (flatTarget && flatTarget !== 'null' && flatTarget !== 'undefined') {
                return parseInt(flatTarget, 10);
            }
            const cached = localStorage.getItem('aura_user_data');
            if (cached) return JSON.parse(cached).aura_target_completion_time || null;
        } catch (e) {}
        return null;
    });

    const [generationType, setGenerationType] = useState<'new' | 'update' | null>(() => {
        try {
            const flatGenType = localStorage.getItem('aura_generation_type');
            if (flatGenType && flatGenType !== 'null' && flatGenType !== 'undefined') {
                return flatGenType as 'new' | 'update';
            }
            const cached = localStorage.getItem('aura_user_data');
            if (cached) return JSON.parse(cached).aura_generation_type || null;
        } catch (e) {}
        return null;
    });

    const [needsDietReveal, setNeedsDietReveal] = useState<boolean>(() => {
        try {
            const cached = localStorage.getItem('aura_user_data');
            if (cached) return JSON.parse(cached).needs_diet_reveal || false;
        } catch (e) {}
        return false;
    });

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isSyncingCloud, setIsSyncingCloud] = useState<boolean>(true);

    const fetchCloudState = useCallback(async () => {
        if (authLoading) {
            return;
        }

        if (!user) {
            setIsLoading(false);
            setIsSyncingCloud(false);
            return;
        }

        setIsSyncingCloud(true);
        try {
            if (user.id === 'mock-user-id-123') {
                const mockStats = JSON.parse(localStorage.getItem('aura_user_data') || '{}');
                setStatus((mockStats.aura_diet_status as DietStatus) || 'idle');
                setPlan(mockStats.nutritionPlan || null);
                setProcessStartTime(mockStats.aura_diet_process_start || null);
                setTargetCompletionTime(mockStats.aura_target_completion_time || null);
                setNeedsDietReveal(mockStats.needs_diet_reveal || false);
                setGenerationType(mockStats.aura_generation_type || null);
                setIsLoading(false);
                setIsSyncingCloud(false);
                return;
            }

            const { data: profile, error } = await supabase
                .from('profiles')
                .select('stats, aura_diet_status, aura_diet_process_start, aura_target_completion_time')
                .eq('id', user.id)
                .single();

            if (error || !profile) {
                setIsLoading(false);
                setIsSyncingCloud(false);
                return;
            }

            const stats = profile.stats || {};
            const cloudStatus = profile.aura_diet_status || 'idle';
            
            let cloudPlan = stats.nutritionPlan || stats.diet || null;
            if (typeof cloudPlan === 'string') {
                try { cloudPlan = JSON.parse(cloudPlan); } catch (e) {}
            }
            if (typeof cloudPlan === 'string') {
                try { cloudPlan = JSON.parse(cloudPlan); } catch (e) {}
            }

            let cloudStart = profile.aura_diet_process_start || stats.aura_diet_process_start;
            let cloudTarget = profile.aura_target_completion_time || stats.aura_target_completion_time;
            
            if (typeof cloudStart === 'string') {
                const numStart = Number(cloudStart);
                cloudStart = isNaN(numStart) ? new Date(cloudStart).getTime() : numStart;
            }
            if (typeof cloudTarget === 'string') {
                const numTarget = Number(cloudTarget);
                cloudTarget = isNaN(numTarget) ? new Date(cloudTarget).getTime() : numTarget;
            }

            const cloudNeedsReveal = stats.needs_diet_reveal === true;
            const cloudGenType = stats.aura_generation_type || null;

            // V6036: OPTIMISTIC STATE SHIELD
            // If the local cache says we just triggered an update (in the last 15 seconds),
            // we IGNORE the cloud's 'completed' status to give the mark-seen Edge Function
            // time to update the DB. Otherwise, the UI will unblock instantly and the user
            // will miss the loading screen.
            let effectiveStatus = cloudStatus;
            let effectiveStart = cloudStart;
            let effectiveTarget = cloudTarget;
            let effectiveGenType = cloudGenType;

            const hasPhotos = stats.photos?.front && stats.photos?.side && stats.photos?.back;
            const hasPlan = !!cloudPlan;

            if (isPremium && !hasPhotos && !hasPlan) {
                effectiveStatus = 'pending_photos';
                effectiveStart = null;
                effectiveTarget = null;
            } else if (effectiveStatus === 'pending_photos' && hasPlan) {
                effectiveStatus = 'completed';
                // Trigger async self-healing in database to recover from pre-fix states
                supabase.from('profiles').update({
                    aura_diet_status: 'completed',
                    aura_diet_process_start: null,
                    aura_target_completion_time: null
                }).eq('id', user.id).then(({ error }) => {
                    if (error) console.error("[useDietStatus] Failed to heal pending_photos status to completed in DB:", error);
                });
            } else {
                // V6033: CLOUD STALE GUARD
                // If the database is stuck in processing but the timer has expired, 
                // force it to completed locally so we don't flash the loading screen.
                // DietGlobalWorker will handle the actual DB fallback.
                if (effectiveStatus === 'processing' || effectiveStatus === 'updating' || effectiveStatus === 'generating_initial' || effectiveStatus === 'trigger_update') {
                    if (effectiveTarget && Date.now() >= effectiveTarget) {
                        console.log("[useDietStatus] Cloud state is stuck in processing but timer expired. Forcing local completion to prevent flash.");
                        effectiveStatus = 'completed';
                    }
                }
            }
            
            try {
                const cached = localStorage.getItem('aura_user_data');
                if (cached) {
                    const parsed = JSON.parse(cached);
                    
                    // V7000: BULLETPROOF TIMER SHIELD
                    // If the backend somehow returned null for the start time, but our local cache
                    // still has a valid ticking timer, we MUST trust the local cache to prevent resets to 0%.
                    const flatStart = localStorage.getItem('aura_diet_process_start');
                    const flatTarget = localStorage.getItem('aura_target_completion_time');
                    const localStart = flatStart ? parseInt(flatStart, 10) : parsed.aura_diet_process_start;
                    const localTarget = flatTarget ? parseInt(flatTarget, 10) : parsed.aura_target_completion_time;

                    if (!effectiveStart && localStart && localTarget) {
                        // If the timer hasn't finished yet, trust it completely.
                        if (Date.now() < localTarget && Date.now() > localStart) {
                            console.log("[useDietStatus] Database lost process_start. Falling back to local bulletproof timer.");
                            effectiveStart = localStart;
                            effectiveTarget = localTarget;
                            effectiveGenType = parsed.aura_generation_type || effectiveGenType;
                        }
                    }

                    const localStatus = localStorage.getItem('aura_diet_status') || parsed.aura_diet_status;
                    if (localStatus === 'trigger_update' && localStart) {
                        if (Date.now() - localStart < 90000) {
                            console.log("[useDietStatus] Shielding optimistic trigger_update state from slow cloud sync.");
                            effectiveStatus = 'trigger_update';
                            effectiveStart = localStart;
                            effectiveTarget = localTarget;
                            effectiveGenType = parsed.aura_generation_type;
                        }
                    }
                }
            } catch (e) {}

            setStatus(effectiveStatus as DietStatus);
            setPlan(cloudPlan);
            setProcessStartTime(effectiveStart);
            setTargetCompletionTime(effectiveTarget);
            setNeedsDietReveal(cloudNeedsReveal);
            if (cloudGenType === 'new' || cloudGenType === 'update') setGenerationType(cloudGenType);

            // V6040: BULLETPROOF REVEAL RESET
            // Whenever we detect a fresh generation state, explicitly wipe the reveal blockers 
            // so the user is guaranteed to see the cinematic overlay when it finishes.
            if (effectiveStatus === 'generating_initial' || effectiveStatus === 'processing' || effectiveStatus === 'trigger_update' || effectiveStatus === 'updating') {
                sessionStorage.removeItem('aura_diet_chat_revealed');
                localStorage.removeItem('aura_pending_completion_reveal');
            }

            // Update fast read-only cache
            if (cloudPlan) {
                localStorage.setItem(`aura_current_diet_${user.id}`, JSON.stringify(cloudPlan));
            }
            
            // V6030: Fix loading screen flash! 
            // The backend updates the root columns (aura_diet_status), but leaves the stale values inside the stats JSON.
            // We MUST override the stats values with the true root values before saving to the local cache, 
            // otherwise the next component mount will initialize with the stale 'processing' state.
            const cachePayload = {
                ...stats,
                aura_diet_status: effectiveStatus,
                aura_diet_process_start: effectiveStart,
                aura_target_completion_time: effectiveTarget,
                aura_generation_type: effectiveGenType,
                needs_diet_reveal: cloudNeedsReveal
            };
            localStorage.setItem('aura_user_data', JSON.stringify(cachePayload));

            setIsLoading(false);
            setIsSyncingCloud(false);
        } catch (e) {
            console.error("[useDietStatus] Error fetching cloud state:", e);
            setIsLoading(false);
            setIsSyncingCloud(false);
        }
    }, [user, authLoading]);

    useEffect(() => {
        let isMounted = true;
        fetchCloudState();

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible' && isMounted) {
                fetchCloudState();
            }
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);
        
        const handleLocalUpdate = () => {
            if (isMounted) fetchCloudState();
        };
        window.addEventListener('aura_diet_updated', handleLocalUpdate);

        return () => {
            isMounted = false;
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('aura_diet_updated', handleLocalUpdate);
        };
    }, [fetchCloudState]);

    const triggerGeneration = async (isUpdate = false) => {
        if (!user) return;
        // V7009: OFFLINE BLOCKER FOR DIET GENERATION
        if (!navigator.onLine) {
            alert("No tienes conexión a internet. Conéctate para poder enviar tu perfil al servidor y que Marc recalcule tu dieta.");
            return;
        }

        const now = Date.now();
        const nextStatus = isUpdate ? 'trigger_update' : 'generating_initial';

        // 90 mins for initial, 30 mins for update
        const waitTime = isUpdate ? 1800000 : 5400000;
        const optimisticTarget = now + waitTime;

        // Optimistic UI
        setStatus(nextStatus as DietStatus);
        setProcessStartTime(now);
        setTargetCompletionTime(optimisticTarget);
        setGenerationType(isUpdate ? 'update' : 'new');
        setNeedsDietReveal(false);

        // Cloud Write
        if (user.id !== 'mock-user-id-123') {
            const { data: currentProfile } = await supabase.from('profiles').select('stats').eq('id', user.id).single();
            const currentStats = currentProfile?.stats || {};
            
            // V1009: Synchronously update localStorage so the UI hydrates the timer IMMEDIATELY
            const cachedStatsStr = localStorage.getItem('aura_user_data');
            if (cachedStatsStr) {
                try {
                    const mockStats = JSON.parse(cachedStatsStr);
                    const updatedStats = {
                        ...mockStats,
                        aura_diet_status: nextStatus,
                        aura_diet_process_start: now,
                        aura_target_completion_time: optimisticTarget,
                        aura_generation_type: isUpdate ? 'update' : 'new',
                        needs_diet_reveal: false
                    };
                    localStorage.setItem('aura_user_data', JSON.stringify(updatedStats));
                } catch (e) {}
            }
            
            await supabase.from('profiles').update({
                stats: {
                    ...currentStats,
                    aura_diet_status: nextStatus,
                    aura_diet_process_start: now,
                    aura_target_completion_time: optimisticTarget,
                    aura_generation_type: isUpdate ? 'update' : 'new',
                    needs_diet_reveal: false
                },
                aura_diet_status: nextStatus,
                aura_diet_process_start: now,
                aura_target_completion_time: optimisticTarget
            }).eq('id', user.id);
        } else {
            const mockStats = JSON.parse(localStorage.getItem('aura_user_data') || '{}');
            const updatedStats = {
                ...mockStats,
                aura_diet_status: nextStatus,
                aura_diet_process_start: now,
                aura_target_completion_time: optimisticTarget,
                aura_generation_type: isUpdate ? 'update' : 'new',
                needs_diet_reveal: false
            };
            localStorage.setItem('aura_user_data', JSON.stringify(updatedStats));
        }
        window.dispatchEvent(new Event('aura_diet_updated'));
    };

    const clearDietReveal = async () => {
         if (!user) return;
         setNeedsDietReveal(false);
         if (user.id !== 'mock-user-id-123') {
             const { data: currentProfile } = await supabase.from('profiles').select('stats').eq('id', user.id).single();
             const currentStats = currentProfile?.stats || {};
             
             // V6009: CRITICAL FIX - We MUST clear the completion timestamps here.
             // If we don't, the next time the user generates a diet, the UI will briefly mount 
             // with the OLD timestamp from yesterday, causing the progress bar to instantly jump to 100% 
             // and break the local timer state before correcting itself.
             if (currentStats.aura_target_completion_time) delete currentStats.aura_target_completion_time;
             if (currentStats.aura_diet_process_start) delete currentStats.aura_diet_process_start;
             
             await supabase.from('profiles').update({
                 aura_diet_status: 'completed',
                 stats: {
                     ...currentStats,
                     needs_diet_reveal: false
                 },
                 aura_target_completion_time: null,
                 aura_diet_process_start: null
             }).eq('id', user.id);
             
             // Clean localStorage immediately to prevent UI flash
             const cachedStr = localStorage.getItem('aura_user_data');
             if (cachedStr) {
                 try {
                     const c = JSON.parse(cachedStr);
                     c.needs_diet_reveal = false;
                     c.aura_diet_status = 'completed';
                     delete c.aura_target_completion_time;
                     delete c.aura_diet_process_start;
                     if (c.stats) {
                         delete c.stats.aura_target_completion_time;
                         delete c.stats.aura_diet_process_start;
                     }
                     localStorage.setItem('aura_user_data', JSON.stringify(c));
                 } catch (e) {}
             }
             // Clear DietGlobalWorker tracking to prevent zombie loops
             sessionStorage.removeItem('aura_diet_prev_status');
         } else {
             const mockStats = JSON.parse(localStorage.getItem('aura_user_data') || '{}');
             mockStats.needs_diet_reveal = false;
             delete mockStats.aura_target_completion_time;
             delete mockStats.aura_diet_process_start;
             localStorage.setItem('aura_user_data', JSON.stringify(mockStats));
         }
    };

    const resetStatus = async () => {
        if (!user) return;
        setStatus('idle');
        setPlan(null);
        if (user.id !== 'mock-user-id-123') {
            const { data: currentProfile } = await supabase.from('profiles').select('stats').eq('id', user.id).single();
            const currentStats = currentProfile?.stats || {};
            await supabase.from('profiles').update({
                stats: {
                    ...currentStats,
                    nutritionPlan: null,
                    needs_diet_reveal: false
                },
                aura_diet_status: 'idle',
                aura_diet_process_start: null,
                aura_target_completion_time: null
            }).eq('id', user.id);
        } else {
            const mockStats = JSON.parse(localStorage.getItem('aura_user_data') || '{}');
            mockStats.aura_diet_status = 'idle';
            mockStats.nutritionPlan = null;
            mockStats.aura_diet_process_start = null;
            mockStats.aura_target_completion_time = null;
            mockStats.needs_diet_reveal = false;
            localStorage.setItem('aura_user_data', JSON.stringify(mockStats));
        }
        window.dispatchEvent(new Event('aura_diet_updated'));
    };

    const isActiveStatus = status === 'generating_initial' || status === 'processing' || status === 'updating' || status === 'trigger_update' || status === 'error_generation' || status === 'error_finalization';
    const isFutureStart = processStartTime !== null && currentTime < processStartTime;
    
    // V6055: Expose a simulated completed/idle status if the cloud process hasn't visually started yet
    const exposedStatus = (isActiveStatus && isFutureStart)
        ? (plan ? 'completed' : 'idle')
        : status;

    const isGenerating = !isFutureStart && (
        status === 'generating_initial' || 
        status === 'processing' || 
        status === 'updating' || 
        status === 'trigger_update' || 
        status === 'error_generation' ||
        status === 'error_finalization' ||
        (status === 'completed' && targetCompletionTime !== null && targetCompletionTime > currentTime)
    );

    // Synchronize flat localStorage status so BottomNav and other components are in sync
    useEffect(() => {
        if (user) {
            let changed = false;

            const currentFlat = localStorage.getItem('aura_diet_status');
            if (currentFlat !== exposedStatus) {
                localStorage.setItem('aura_diet_status', exposedStatus);
                changed = true;
            }

            // Sync flat process start time so BottomNav knows if the timer has visually started
            const currentStart = localStorage.getItem('aura_diet_process_start');
            const expectedStart = processStartTime ? String(processStartTime) : '';
            if (currentStart !== expectedStart) {
                if (expectedStart) {
                    localStorage.setItem('aura_diet_process_start', expectedStart);
                } else {
                    localStorage.removeItem('aura_diet_process_start');
                }
                changed = true;
            }

            // Sync flat target completion time so BottomNav has the wait timer value
            const currentTarget = localStorage.getItem('aura_target_completion_time');
            const expectedTarget = targetCompletionTime ? String(targetCompletionTime) : '';
            if (currentTarget !== expectedTarget) {
                if (expectedTarget) {
                    localStorage.setItem('aura_target_completion_time', expectedTarget);
                } else {
                    localStorage.removeItem('aura_target_completion_time');
                }
                changed = true;
            }

            if (changed) {
                window.dispatchEvent(new Event('aura_diet_updated'));
            }
        }
    }, [exposedStatus, processStartTime, targetCompletionTime, user]);

    return {
        status: exposedStatus,
        plan,
        isLoading,
        isSyncingCloud,
        hasValidPlan: !!plan && !!plan.comidas && plan.comidas.length > 0,
        isGenerating,
        processStartTime,
        targetCompletionTime,
        generationType,
        needsDietReveal,
        triggerGeneration,
        resetStatus,
        clearDietReveal
    };
};
